import React, { useEffect, useState } from 'react';
import { Button, Grid2 as Grid, InputAdornment, TextField, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import appConfig from '../../config/appConfig';

const TieredPriceInput = ({ formData, onChange, disabled = false }) => {
  const [tiers, setTiers] = useState(formData.tiers);

  const onUpdatingUnitAmount = (i, unit_amount) => {
    setTiers((prevTiers) => prevTiers.map((tier, index) => (index === i ? { ...tier, unit_amount } : tier)));
  };

  const onUpdatingFlatAmount = (i, flat_amount) => {
    setTiers((prevTiers) => prevTiers.map((tier, index) => (index === i ? { ...tier, flat_amount } : tier)));
  };

  const onUpdatingUpTo = (i, up_to) => {
    setTiers((prevTiers) => {
      return prevTiers.map((tier, index) => {
        if (index === i) {
          return { ...tier, up_to };
        } else if (index > i) {
          return { ...tier, starting_from: up_to + 1, up_to: 'inf' };
        } else {
          return tier;
        }
      });
    });
  };

  const addAnotherTier = () => {
    setTiers((prevTiers) => {
      let lastItemIndex = prevTiers.length - 1;

      return [
        ...prevTiers.map((tier, index) =>
          index === lastItemIndex
            ? {
                ...tier,
                up_to: prevTiers[lastItemIndex].starting_from,
              }
            : tier,
        ),
        {
          starting_from:
            prevTiers[lastItemIndex].up_to === 'inf'
              ? prevTiers[lastItemIndex].starting_from + 1
              : prevTiers[lastItemIndex].up_to + 1,
          up_to: 'inf',
          unit_amount: 0,
          flat_amount: 0,
        },
      ];
    });
  };

  const onDeletingTier = (index) => {
    setTiers((prevTiers) => {
      prevTiers[index + 1].starting_from = prevTiers[index].starting_from;
      return prevTiers.filter((tier, i) => i !== index);
    });
  };

  useEffect(() => {
    onChange({ ...formData, tiers });
  }, [formData, onChange, tiers]);

  return (
    <>
      <Grid container spacing={1} sx={{ mt: '2rem', mb: '0.3rem' }}>
        <Grid size={{ xs: 2.5, sm: 2.5, md: 2 }} alignItems="left">
          <Typography variant="h7">Starting From</Typography>
        </Grid>
        <Grid size={{ xs: 2.5, sm: 2.5, md: 2 }} alignItems="left">
          <Typography variant="h7">Up To</Typography>
        </Grid>
        <Grid size={{ xs: 3, sm: 3, md: 2 }} alignItems="left">
          <Typography variant="h7">Per Document</Typography>
        </Grid>
        <Grid size={{ xs: 3, sm: 3, md: 2 }} alignItems="left">
          <Typography variant="h7">Flat Fee</Typography>
        </Grid>
      </Grid>
      {tiers.map((tier, index) => (
        <Grid container spacing={1} key={index} sx={{ mb: '0.5rem' }}>
          <Grid size={{ xs: 2.5, sm: 2.5, md: 2 }}>
            <TextField
              type="text"
              required
              placeholder="Enter Your First Unit"
              value={new Intl.NumberFormat(window.navigator.language).format(tier.starting_from)}
              size="small"
              disabled
              sx={{ mt: 0 }}
            />
          </Grid>
          <Grid size={{ xs: 2.5, sm: 2.5, md: 2 }}>
            {tier.up_to === 'inf' ? (
              <TextField type="text" value="∞" required placeholder="Enter Your Last Unit" size="small" disabled />
            ) : (
              <TextField
                type="text"
                value={new Intl.NumberFormat(window.navigator.language).format(tier.up_to)}
                required
                placeholder="Enter Your Last Unit"
                error={tier.up_to < tier.starting_from}
                helperText={tier.up_to < tier.starting_from ? 'Overlapping tier' : ''}
                size="small"
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  onUpdatingUpTo(index, value === '' ? '' : parseInt(value, 10) || 0);
                }}
                disabled={index !== tiers.length - 2 || disabled}
              />
            )}
          </Grid>
          <Grid size={{ xs: 3, sm: 3, md: 2 }}>
            <TextField
              type="number"
              value={tier.unit_amount}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9.]/g, '');
                onUpdatingUnitAmount(index, Number(value));
              }}
              onKeyDown={(e) => {
                if (e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') {
                  e.preventDefault();
                }
              }}
              required
              placeholder="Enter price per document"
              size="small"
              disabled={disabled}
              slotProps={{
                input: {
                  startAdornment: <InputAdornment position="start">{appConfig.stripeCurrency}</InputAdornment>,
                },
              }}
            />
          </Grid>
          <Grid size={{ xs: 3, sm: 3, md: 2 }}>
            <TextField
              type="number"
              value={tier.flat_amount}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9.]/g, '');
                onUpdatingFlatAmount(index, Number(value));
              }}
              onKeyDown={(e) => {
                if (e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') {
                  e.preventDefault();
                }
              }}
              required
              placeholder="Enter Your Flat Fee"
              size="small"
              disabled={disabled}
              slotProps={{
                input: {
                  startAdornment: <InputAdornment position="start">{appConfig.stripeCurrency}</InputAdornment>,
                },
              }}
            />
          </Grid>
          <Grid size={1}>
            {index === tiers.length - 2 ? (
              <Button
                variant="outlined"
                color="error"
                size="medium"
                onClick={() => onDeletingTier(index)}
                disabled={disabled}
              >
                <DeleteIcon />
              </Button>
            ) : null}
          </Grid>
        </Grid>
      ))}
      <Grid>
        <Grid size={3}>
          <Button variant="outlined" onClick={addAnotherTier} disabled={disabled}>
            Add another tier
          </Button>
        </Grid>
      </Grid>
    </>
  );
};

export default TieredPriceInput;
